import PageLayout from "Layouts/PageLayout";
import {useTranslation} from "react-i18next";
import CalculatorIcon from "../../Assets/SVG/CalculatorIcon";
import Separator from "Components/Separator";
import List from "../../Components/List/List";
import React, {useMemo, useRef} from "react";
import {formatNumber} from "../../Utils/NumberUtils";
import {ZettleSalesItemDto} from "../../Apis/Models/ZettleSalesDto";
import Label from "../../Components/Label";
import Select from "../../Components/Select";
import {IQueryParam} from "../../Apis/Models/IQueryParams";
import useFilteredList from "../../Hooks/UseFilteredList";
import useQueryParams from "../../Hooks/UseQueryParams";
import DatePicker from "../../Components/DatePicker";
import moment from "moment";
import {Bar, BarChart, Tooltip, XAxis, YAxis} from "recharts";
import InventoryIcon from "../../Assets/SVG/InventoryIcon";
import MoneyIcon from "../../Assets/SVG/MoneyIcon";
import {TotalComponentV2} from "../../Components/TotalComponent";
import {useSettlementProjects, useZettleSales} from "../../Apis/ProjectSettlementApi";
import {canAccess} from "../../Utils/CanAccess";
import {useUserContext} from "../../Providers/UserContextProvider";

const ZettleSales = () => {

    const {t} = useTranslation();

    const [filter, setFilter] = useQueryParams();
    const {user} = useUserContext();

    const chartsDivRef = useRef<HTMLDivElement>(null)

    const isAdmin = canAccess(user.permissions, 'Admin')

    const {data: projects} = useSettlementProjects();
    const {data: zettleSales, isLoading} = useZettleSales(filter);

    const onlyUnknowns = useMemo(() => [
        {value: 'false', label: t('no')},
        {value: 'true', label: t('yes')},
    ], [t]);

    const [filteredProjects, setProjectFilter, projectFilterValue] = useFilteredList(projects ?? [], x => [x.name]);
    const [filteredUnknowns, setFilteredUnknowns] = useFilteredList(onlyUnknowns, x => [x.label]);

    const setQueryField = (fieldName: 'from' | 'to' | 'projectId' | 'onlyUnknown', value: IQueryParam) => {
        const newValue = {...filter, [fieldName]: value}
        setFilter(newValue, true);
    }

    const salesColumns = [
        {
            title: t('productName'),
            textRender: (record: ZettleSalesItemDto) => record.productName,
        },
        {
            title: t('variantName'),
            textRender: (record: ZettleSalesItemDto) => record.variantName,
        },
        {
            title: t('category'),
            textRender: (record: ZettleSalesItemDto) => record.categoryName,
        },
        {
            title: t('quantity'),
            textRender: (record: ZettleSalesItemDto) => record.quantity?.toString(),
        },
        {
            title: t('totalDiscounts'),
            textRender: (record: ZettleSalesItemDto) => formatNumber(record.totalDiscounts),
        },
        {
            title: t('totalSalesExclTax'),
            textRender: (record: ZettleSalesItemDto) => formatNumber(record.totalSalesPriceExclVat),
        },
        {
            title: t('totalTax'),
            textRender: (record: ZettleSalesItemDto) => formatNumber(record.totalVat),
        },
        {
            title: t('totalSalesInclTax'),
            textRender: (record: ZettleSalesItemDto) => formatNumber(record.totalSalesPriceInclVat),
        },
    ]

    return (
        <PageLayout title={t('zettleSales')} icon={<CalculatorIcon/>}>
            <div>

                <div style={{display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 20}}>

                    <div>
                        <Label placeholder={t('from')}/>
                        <DatePicker name={'from'}
                                    onChange={value => setQueryField('from', value ? moment(value).format('YYYY-MM-DD') : '')}
                                    value={!!filter['from'] ? filter['from'].toString() : undefined}/>
                    </div>

                    <div>
                        <Label placeholder={t('to')}/>
                        <DatePicker name={'to'}
                                    onChange={value => setQueryField('to', value ? moment(value).format('YYYY-MM-DD') : '')}
                                    value={!!filter['to'] ? filter['to'].toString() : undefined}/>
                    </div>

                    <div>
                        <Label placeholder={t('project')}/>
                        <Select name={"projectId"} value={filter['projectId']}
                                onChange={value => setQueryField('projectId', value)} onFilter={setProjectFilter}>
                            {!projectFilterValue && (
                                <Select.Option value={undefined} label={isAdmin ? t('all') : t('select')}/>
                            )}
                            {filteredProjects?.map(project => <Select.Option key={project.id} value={project.id}
                                                                             label={project.name}/>)}
                        </Select>
                    </div>

                    {isAdmin && (
                        <div>
                            <Label placeholder={t('onlyUnknown')}/>
                            <Select name={"onlyUnknown"} value={filter['onlyUnknown']}
                                    onChange={value => setQueryField('onlyUnknown', value)}
                                    onFilter={setFilteredUnknowns}>
                                {filteredUnknowns?.map(option => <Select.Option key={option.value}
                                                                                value={option.value}
                                                                                label={option.label}/>)}
                            </Select>
                        </div>
                    )}
                </div>

                <Separator style={{margin: '30px 0'}}/>
                <div>
                    {t('allPricesAreExclVatMessage')}
                </div>
                <Separator style={{margin: '30px 0'}}/>

                <div ref={chartsDivRef}>
                    <BarChart width={chartsDivRef.current?.clientWidth} height={200}
                              data={zettleSales?.salesByDate ?? []}>
                        <YAxis tickCount={5} axisLine={false} tickLine={false}/>
                        <XAxis axisLine={false} tickLine={false} dataKey="date" style={{fontWeight: '700'}}/>
                        <Bar dataKey="count" fill="#E91D25" style={{borderRadius: '5px 5px 0 0'}}
                             name={t('numberOfSales').toString()}/>
                        <Bar dataKey="itemCount" fill="#707070" style={{borderRadius: '5px 5px 0 0'}}
                             name={t('numberOfItemsSold').toString()}/>
                        <Tooltip/>
                    </BarChart>
                </div>

                <Separator style={{margin: '30px 0'}}/>

                <List<ZettleSalesItemDto> data={zettleSales?.salesItems} loading={isLoading}
                                          columns={salesColumns}
                                          keyExtractor={(record) => `${record.productSku}-${record.productName}-${record.variantName}`}/>
                <TotalComponentV2
                    items={[
                        {
                            icon: <InventoryIcon/>,
                            header: t('quantity'),
                            total: zettleSales?.salesItems.reduce((acc, curr) => acc + curr.quantity, 0).toString() ?? ''
                        },
                        {
                            icon: <MoneyIcon/>,
                            header: t('total'),
                            total: `${formatNumber(zettleSales?.salesItems.reduce((acc, curr) => acc + curr.totalSalesPriceExclVat, 0) ?? 0)} DKK`
                        },
                        {
                            icon: <MoneyIcon/>,
                            header: t('totalInclVat'),
                            total: `${formatNumber(zettleSales?.salesItems.reduce((acc, curr) => acc + curr.totalSalesPriceInclVat, 0) ?? 0)} DKK`
                        },
                    ]}/>
            </div>
        </PageLayout>
    )
}

export default ZettleSales
